import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { adminGet, adminPatch } from "../../lib/adminApi";
import { useUiPreference } from "../../lib/uiPreferences";
import { PageHeader, ErrorBanner, OvergeslagenBanner, PaginationBar, formatDate } from "../../components/admin/AdminUI";
import ViewModeToggle from "../../components/admin/ViewModeToggle";
import { Button, Card, Input, Label } from "../../components/fulkra";
import { TH, TD, RIJ } from "../../components/fulkra/tabel";
import { PIJN_LOCATIE_LABELS, PIJN_ZIJDE_LABELS } from "../../lib/constants";
import {
  SIGNAAL_UITLEG, NIVEAU_UITLEG,
  noemNetjes, perTweeZinnen, signaalNiveau, hoogsteNiveau,
  signaalZin, splitsVerbeteringen,
} from "../../lib/namen";

/** Voorletters en achternaam als één naam.
 *
 * Staat sinds 22-08-2026 bij het signaal: een klantnummer alleen zegt niets als je door
 * je werklijst loopt. Dit is het beheerscherm; de werkgever ziet deze gegevens nooit.
 * Ontbreekt de naam (bijvoorbeeld na AVG-anonimisering), dan blijft alleen het nummer
 * staan in plaats van een lege regel.
 */
function naamVan(f) {
  const delen = [f?.voorletters, f?.achternaam].map((d) => (d || "").trim()).filter(Boolean);
  return delen.join(" ");
}

/**
 * De plek op de voet, alleen als de aanmeter er één heeft aangewezen.
 *
 * Tot 25-08-2026 stond hier "Voet — zelfrapport" zodra er géén plek was — bij elk signaal
 * uit een vragenlijst dus. Steven: "zelf rapporteren mag je weg laten". Een deelnemer die
 * een cijfer invult wijst geen plek aan; dan valt er over de plek niets te melden.
 */
function locatieVan(f) {
  if (!f?.locatie) return null;
  const plek = noemNetjes(f.locatie, PIJN_LOCATIE_LABELS);
  if (!f.zijde) return plek;
  return `${plek} (${noemNetjes(f.zijde, PIJN_ZIJDE_LABELS).toLowerCase()})`;
}

/**
 * Signalen voor de podotherapeut, in de huisstijl.
 *
 * Restyling, geen herbouw: dezelfde filters (die in het adres blijven staan), dezelfde
 * groepering per werkgever, dezelfde twee weergaven, dezelfde afhandel-stroom met de eis
 * van minimaal vijf tekens, en alle data-testid's ongewijzigd.
 *
 * Eén ding is inhoudelijk toegevoegd, omdat het ontwerp het voorschrijft: de melding
 * bovenaan dat dit medische gegevens zijn en dat een werkgever hier niets van ziet. Dat
 * is geen verzonnen tekst maar de PrivacyNotice uit het designsysteem, en het klopt met
 * hoe de app werkt.
 */

function isOpen(f) {
  return f && f.afgehandeld_op == null;
}

/** Pijncijfer: 9-10 is alarmerend, 7-8 verhoogd, daaronder aandacht. Betekeniskleur,
    dus nooit decoratief — het cijfer bepaalt de kleur, niets anders. */
function pijnStijl(score) {
  if (score >= 9) return { background: "var(--alarm)", color: "#fff" };
  if (score >= 7) return { background: "var(--alarm-zacht)", color: "var(--text-alarm)" };
  return { background: "var(--aandacht-zacht)", color: "var(--text-aandacht)" };
}

const KEUZEVELD = {
  minHeight: "var(--veld-hoogte-desktop)", borderRadius: "var(--radius)",
  border: "1px solid var(--border-subtle)", background: "var(--wit)",
  color: "var(--text-strong)", padding: "0 var(--ruimte-5)",
  fontSize: "var(--fs-body)", fontFamily: "var(--font-tekst)", boxShadow: "var(--schaduw-1)",
};


const MERKJE = {
  display: "inline-flex", alignItems: "center", borderRadius: "var(--radius)",
  padding: "2px var(--ruimte-4)", fontSize: "var(--fs-xs)", fontWeight: "var(--fw-medium)",
  lineHeight: 1.3, whiteSpace: "nowrap",
};

export default function PijnFlagsPage() {
  const [sp, setSp] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const [view, setView] = useUiPreference("pijn_signalen_view", "blokjes");
  const LIMIT = 100;
  const filterStatus = sp.get("status") || "open";
  const filterFase = sp.get("fase") || "";
  const werknemerId = sp.get("werknemer_id") || "";
  const werkgeverId = sp.get("werkgever_id") || "";
  const pijncijferMin = sp.get("pijncijfer_min") || "";
  const datumFrom = sp.get("datum_from") || "";
  const datumTo = sp.get("datum_to") || "";
  const [openFlag, setOpenFlag] = useState(null);
  const qc = useQueryClient();

  const werkgeversQ = useQuery({
    queryKey: ["werkgevers", "for-filter"],
    queryFn: () => adminGet("/api/admin/werkgevers", { limit: 100 }),
  });

  const params = { limit: LIMIT, offset };
  if (filterStatus === "open") params.afgehandeld = false;
  else if (filterStatus === "afgehandeld") params.afgehandeld = true;
  if (filterFase) params.fase = filterFase;
  if (werknemerId) params.werknemer_id = werknemerId;
  if (werkgeverId) params.werkgever_id = werkgeverId;
  if (pijncijferMin) params.pijncijfer_min = parseInt(pijncijferMin, 10);
  if (datumFrom) params.datum_from = datumFrom;
  if (datumTo) params.datum_to = datumTo;

  const q = useQuery({
    queryKey: ["pijn-flags", params],
    queryFn: () => adminGet("/api/admin/pijn-flags", params),
    keepPreviousData: true,
  });

  const handleMut = useMutation({
    mutationFn: ({ id, body }) => adminPatch(`/api/admin/pijn-flags/${id}/afhandelen`, body),
    onSuccess: () => {
      toast.success("Signaal afgehandeld");
      setOpenFlag(null);
      qc.invalidateQueries({ queryKey: ["pijn-flags"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Afhandelen mislukt"),
  });

  const items = q.data?.items || q.data || [];
  const total = q.data?.total ?? items.length;

  const groepen = useMemo(() => {
    const map = new Map();
    for (const f of items) {
      const key = f.werkgever_naam || "Geen werkgever";
      if (!map.has(key)) map.set(key, { naam: key, items: [] });
      map.get(key).items.push(f);
    }
    /* Binnen de werkgever ook per deelnemer bundelen. Zonder dit krijgt iemand met vijf
       signalen vijf losse kaarten met vijf keer dezelfde naam eronder — en dan lijkt het
       alsof het om vijf mensen gaat. */
    for (const groep of map.values()) {
      const perDeelnemer = new Map();
      for (const f of groep.items) {
        const sleutel = f.werknemer_id || f.klantnummer || "onbekend";
        if (!perDeelnemer.has(sleutel)) {
          perDeelnemer.set(sleutel, {
            werknemer_id: f.werknemer_id,
            klantnummer: f.klantnummer || "",
            naam: naamVan(f) || f.klantnummer || "Onbekende deelnemer",
            items: [],
          });
        }
        perDeelnemer.get(sleutel).items.push(f);
      }
      /* Vooruitgang is geen werk. Een regel als "pijn gedaald van 7 naar 3" heeft geen
         "Pak op"-knop nodig, en met drie van die regels per deelnemer per vervolgmeting
         drukken ze de handvol signalen weg die er wél toe doen (Steven, 25-08-2026).
         Ze verdwijnen daarom uit de lijst en komen terug als slotzin onder het signaal
         dat over hetzelfde onderwerp ging. */
      for (const d of perDeelnemer.values()) {
        d.items = splitsVerbeteringen(d.items).signalen;
      }
      for (const [sleutel, d] of Array.from(perDeelnemer.entries())) {
        if (!d.items.length) perDeelnemer.delete(sleutel);
      }
      groep.items = Array.from(perDeelnemer.values()).flatMap((d) => d.items);
      // De ergste bovenaan: wie het hoogste pijncijfer heeft, wil je als eerste zien.
      groep.deelnemers = Array.from(perDeelnemer.values()).sort((a, b) => {
        const hoogste = (d) => d.items.reduce((h, f) => Math.max(h, f.pijncijfer || 0), 0);
        return hoogste(b) - hoogste(a) || a.naam.localeCompare(b.naam, "nl");
      });
    }
    // Een werkgever waarvan alleen vooruitgang overbleef, heeft niets te tonen.
    return Array.from(map.values())
      .filter((g) => g.deelnemers.length > 0)
      .sort((a, b) => a.naam.localeCompare(b.naam, "nl"));
  }, [items]);

  /* Wat er ná het weglaten van de vooruitgang nog te doen staat. Alleen hierop mag
     "geen signalen" gebaseerd worden — anders toont het scherm een lege lijst terwijl de
     server wel rijen teruggaf. */
  const teTonen = useMemo(
    () => groepen.reduce((n, g) => n + g.items.length, 0),
    [groepen],
  );

  function updateParam(key, value) {
    const ns = new URLSearchParams(sp);
    if (!value) ns.delete(key); else ns.set(key, value);
    setSp(ns);
    setOffset(0);
  }

  return (
    <div data-testid="pijnflags-page">
      <PageHeader
        title="Signalen voor podotherapeut"
        subtitle="Pijnsignalen uit de aanmeetdag-uitvoering, gegroepeerd per werkgever"
        actions={<ViewModeToggle value={view} onChange={setView} testid="pijnflags-viewmode" />}
      />

      <div
        role="note"
        data-testid="pijnflags-privacy"
        style={{
          marginBottom: "var(--ruimte-7)", borderRadius: "var(--radius-vlak)",
          border: "1px solid var(--aandacht-rand)", background: "var(--aandacht-zacht)",
          padding: "var(--ruimte-5) var(--ruimte-6)", color: "var(--text-aandacht)",
        }}
      >
        <div style={{ font: "var(--text-label)", color: "var(--text-aandacht)" }}>Klachtsignalen zijn medische gegevens</div>
        <p style={{ marginTop: 2, fontSize: "var(--fs-body)" }}>
          Deze lijst is alleen zichtbaar voor admin en podotherapeut. Werkgevers zien hier niets van;
          zij zien uitsluitend totalen.
        </p>
      </div>

      <div style={{ marginBottom: "var(--ruimte-6)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }} data-testid="pijnflags-filters">
        <select value={werkgeverId} onChange={(e) => updateParam("werkgever_id", e.target.value)} aria-label="Werkgever-filter" data-testid="pijnflags-werkgever-filter"
          style={{ ...KEUZEVELD, width: 224 }}>
          <option value="">Alle werkgevers</option>
          {(werkgeversQ.data?.items || []).map((w) => (
            <option key={w.werkgever_id} value={w.werkgever_id}>{w.bedrijfsnaam}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => updateParam("status", e.target.value)} aria-label="Status-filter" data-testid="pijnflags-status-filter" style={{ ...KEUZEVELD, width: 150 }}>
          <option value="open">Open</option>
          <option value="afgehandeld">Afgehandeld</option>
          <option value="alle">Alle</option>
        </select>
        <select value={filterFase} onChange={(e) => updateParam("fase", e.target.value)} aria-label="Fase-filter" data-testid="pijnflags-fase-filter" style={{ ...KEUZEVELD, width: 280 }}>
          <option value="">Alle fasen</option>
          <option value="pre_traject">Pre-traject (zelfrapport vooraf)</option>
          <option value="aanmeetdag">Aanmeetdag (door aanmeter)</option>
          <option value="post_start">Post-start (zelfrapport na zolen)</option>
        </select>
        <input type="number" min="0" max="10" placeholder="Min pijn" value={pijncijferMin}
          onChange={(e) => updateParam("pijncijfer_min", e.target.value)} aria-label="Minimum pijncijfer" data-testid="pijnflags-pijncijfer-filter"
          style={{ ...KEUZEVELD, width: 100 }} />
        <input type="date" value={datumFrom} onChange={(e) => updateParam("datum_from", e.target.value)} aria-label="Datum vanaf" data-testid="pijnflags-datum-from" style={{ ...KEUZEVELD, width: 150 }} />
        <input type="date" value={datumTo} onChange={(e) => updateParam("datum_to", e.target.value)} aria-label="Datum tot" data-testid="pijnflags-datum-to" style={{ ...KEUZEVELD, width: 150 }} />
        {werknemerId ? (
          <Button variant="secundair" size="sm" onClick={() => updateParam("werknemer_id", "")} data-testid="pijnflags-clear-werknemer">
            Werknemer-filter wissen
          </Button>
        ) : null}
      </div>

      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="pijnsignaal" meervoud="pijnsignalen"
        testid="pijn-flags-overgeslagen"
      />

      {q.isLoading ? (
        <Leeg>Laden…</Leeg>
      ) : teTonen === 0 ? (
        <Leeg testid="pijnflags-empty">Geen signalen voor deze selectie.</Leeg>
      ) : (
        <div style={{ display: "grid", gap: "var(--ruimte-8)" }}>
          {groepen.map((groep) => (
            <section
              key={groep.naam}
              data-testid={`pijnflag-groep-${groep.naam}`}
              style={{
                background: "var(--wit)", border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius)", boxShadow: "var(--elevation-card)", overflow: "hidden",
              }}
            >
              {/* Inklapbaar per werkgever (Steven, 24-08-2026). Standaard open: je komt op
                  deze pagina juist om te zien wat er ligt. */}
              <details open data-testid={`werkgever-groep-${groep.naam}`}>
                <summary
                  className="uitklapkop"
                  style={{
                    display: "flex", alignItems: "center", gap: "var(--ruimte-3)",
                    cursor: "pointer",
                    borderBottom: "1px solid var(--border-subtle)", background: "var(--surface-sunken)",
                    padding: "var(--ruimte-5) var(--ruimte-6)",
                  }}
                >
                  <span className="uitklap-pijl" aria-hidden="true">&#9656;</span>
                  <span>
                    <span style={{ display: "block", fontFamily: "var(--font-tekst)", fontWeight: "var(--fw-medium)", fontSize: "var(--fs-body)", lineHeight: 1.3, color: "var(--text-strong)" }}>{groep.naam}</span>
                    <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                      {groep.items.length} {groep.items.length === 1 ? "signaal" : "signalen"}
                      {` · ${groep.deelnemers.length} ${groep.deelnemers.length === 1 ? "deelnemer" : "deelnemers"}`}
                    </span>
                  </span>
                </summary>

                {view === "rijen" ? (
                  <PijnSignaalTabel deelnemers={groep.deelnemers} onAfhandelen={setOpenFlag} />
                ) : (
                  <div
                    className="blokjes-raster"
                    data-testid="pijnflags-blokjes"
                    style={{ padding: "var(--ruimte-6)" }}
                  >
                    {groep.deelnemers.map((d) => (
                      <PijnDeelnemerKaart
                        key={d.werknemer_id || d.klantnummer}
                        deelnemer={d}
                        onAfhandelen={setOpenFlag}
                      />
                    ))}
                  </div>
                )}
              </details>
            </section>
          ))}
        </div>
      )}

      <PaginationBar offset={offset} limit={LIMIT} total={total}
        onPrev={() => setOffset(Math.max(0, offset - LIMIT))}
        onNext={() => setOffset(offset + LIMIT)} />

      {openFlag ? (
        <AfhandelenModal
          flag={openFlag}
          onClose={() => setOpenFlag(null)}
          onSubmit={(body) => handleMut.mutate({ id: openFlag.flag_id, body })}
          loading={handleMut.isPending}
        />
      ) : null}
    </div>
  );
}

function Leeg({ children, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
        padding: "var(--ruimte-10)", textAlign: "center",
        fontSize: "var(--fs-body)", color: "var(--text-soft)",
      }}
    >
      {children}
    </div>
  );
}

/* Alleen de fase, als klein label. De toevoeging "(zelfrapport)" is er op 25-08-2026 uit
   gehaald: Steven wil geen twee labels waarvan er één uitlegt waar het vandaan komt.
   Wie het wil weten, ziet het in de tooltip. */
function FaseBadge({ fase }) {
  const map = {
    pre_traject: { label: "Pre-traject", stijl: { background: "var(--neutraal-100)", color: "var(--text-body)" }, titel: "Zelfrapport vóór levering" },
    aanmeetdag: { label: "Aanmeetdag", stijl: { background: "var(--neutraal-100)", color: "var(--text-body)" }, titel: "Door aanmeter geregistreerd" },
    post_start: { label: "Post-start", stijl: { background: "var(--alarm-zacht)", color: "var(--text-alarm)" }, titel: "Zelfrapport na zolen-levering (escalatie)" },
    verbetering: { label: "Verbetering", stijl: { background: "var(--positief-zacht)", color: "var(--text-positief)" }, titel: "Vergelijking baseline vs wk12" },
  };
  const c = map[fase] || { label: fase || "—", stijl: { background: "var(--neutraal-100)", color: "var(--text-body)" }, titel: "" };
  return (
    <span style={{ ...MERKJE, ...c.stijl }} title={c.titel}>
      {c.label}
    </span>
  );
}

function StatusMerkje({ open, testid }) {
  return (
    <span
      data-testid={testid}
      style={{
        ...MERKJE,
        ...(open
          ? { background: "var(--aandacht-zacht)", color: "var(--text-aandacht)" }
          : { background: "var(--positief-zacht)", color: "var(--text-positief)" }),
      }}
    >
      {open ? "Open" : "Afgehandeld"}
    </span>
  );
}

/**
 * Eén rij per deelnemer, uitklapbaar naar zijn losse signalen.
 *
 * Steven, 24-08-2026: "bij D Van Kerkhof staat vijf keer een aparte rij met veel onnuttige
 * info; kunnen we daar één rij van maken met X aantal punten open."
 *
 * Wat eruit is gegaan omdat het vijf keer hetzelfde zei: de kolom Werkgever (die staat al in
 * de kop van de groep), de locatie "Voet — zelfrapport" (bij een zelfrapport is er geen
 * plek, alleen een cijfer) en de fase. Die twee staan nu bij het signaal zelf, uitgeklapt.
 *
 * En het pijncijfer 0 is verdwenen bij regels die niet over een pijncijfer gaan. De engine
 * schrijft daar een 0 weg omdat de kolom een getal wil, maar op het scherm leest dat als
 * "geen pijn gemeten" terwijl het "niet van toepassing" betekent.
 */
function PijnSignaalTabel({ deelnemers, onAfhandelen }) {
  return (
    <div style={{ overflowX: "auto" }} data-testid="pijnflags-tabel">
      {/* Vaste kolombreedtes, om dezelfde reden als op de werknemerslijst: elke werkgever
          heeft zijn eigen tabel, en zonder dit staan de kolommen per blok op een andere plek. */}
      {/* Vaste kolombreedtes, om dezelfde reden als op de werknemerslijst: elke werkgever
          heeft zijn eigen tabel, en zonder dit staan de kolommen per blok op een andere plek.

          Het klantnummer heeft sinds 24-08-2026 een eigen kolom. Het stond onder de naam,
          waardoor elke rij twee regels hoog werd — Steven: "ik vind het nu erg veel ruimte
          innemen." Nu past een deelnemer op één regel. */}
      <table
        style={{
          width: "100%", borderCollapse: "collapse", tableLayout: "fixed",
          minWidth: 760, fontSize: "var(--fs-body)",
        }}
      >
        <colgroup>
          <col />
          <col style={{ width: "11rem" }} />
          <col style={{ width: "10rem" }} />
          <col style={{ width: "8rem" }} />
          <col style={{ width: "11rem" }} />
          <col style={{ width: "6rem" }} />
        </colgroup>
        <thead>
          <tr>
            {["Deelnemer", "Klantnr.", "Openstaand", "Hoogste pijn", "Laatste signaal"].map((k) => (
              <th key={k} scope="col" style={TH}>{k}</th>
            ))}
            <th scope="col" style={{ ...TH, textAlign: "right" }}>Actie</th>
          </tr>
        </thead>
        <tbody>
          {deelnemers.map((d) => (
            <DeelnemerRij
              key={d.werknemer_id || d.klantnummer}
              deelnemer={d}
              onAfhandelen={onAfhandelen}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeelnemerRij({ deelnemer, onAfhandelen }) {
  const [open, setOpen] = useState(false);
  const openstaand = deelnemer.items.filter(isOpen).length;
  const hoogste = deelnemer.items.reduce((h, f) => Math.max(h, f.pijncijfer || 0), 0);
  const laatste = deelnemer.items.reduce(
    (l, f) => (!l || new Date(f.doorgestuurd_op) > new Date(l) ? f.doorgestuurd_op : l),
    null,
  );
  const testid = deelnemer.werknemer_id || deelnemer.klantnummer;
  // De tint van de zwaarste regel die nog openstaat: is alles opgepakt, dan hoeft de rij
  // geen kleur meer te dragen.
  const nogOpen = deelnemer.items.filter(isOpen);
  const niveau = hoogsteNiveau(nogOpen);

  return (
    <>
      <tr
        data-testid={`deelnemer-rij-${testid}`}
        data-niveau={niveau}
        className={`niveau-${niveau}`}
        title={NIVEAU_UITLEG[niveau]}
        style={RIJ}
      >
        <td style={TD}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            data-testid={`deelnemer-uitklappen-${testid}`}
            className="knop-compact"
            style={{
              display: "flex", alignItems: "center", gap: "var(--ruimte-3)",
              background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left",
            }}
            aria-expanded={open}
          >
            <span
              className="uitklap-pijl"
              aria-hidden="true"
              style={{ transform: open ? "rotate(90deg)" : "none" }}
            >
              &#9656;
            </span>
            <span
              style={{
                color: "var(--text-strong)", fontWeight: "var(--fw-medium)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              {deelnemer.naam}
            </span>
          </button>
        </td>
        <td
          style={{ ...TD, fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}
          data-testid={`deelnemer-klantnummer-${testid}`}
        >
          {deelnemer.klantnummer || deelnemer.werknemer_id?.slice(0, 8)}
        </td>
        <td style={TD} data-testid={`deelnemer-open-${testid}`}>
          {openstaand > 0 ? (
            <span style={{ color: "var(--text-strong)", fontWeight: "var(--fw-medium)" }}>
              {openstaand} {openstaand === 1 ? "punt" : "punten"} open
            </span>
          ) : (
            <span style={{ color: "var(--text-soft)" }}>alles opgepakt</span>
          )}
          {deelnemer.items.length !== openstaand ? (
            <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
              {" "}van {deelnemer.items.length}
            </span>
          ) : null}
        </td>
        <td style={TD}>
          {hoogste > 0 ? (
            <span
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 26, height: 22, borderRadius: "var(--radius-pil)",
                fontSize: "var(--fs-xs)", fontWeight: "var(--fw-bold)", fontVariantNumeric: "tabular-nums",
                ...pijnStijl(hoogste),
              }}
            >
              {hoogste}
            </span>
          ) : (
            <span style={{ color: "var(--text-soft)" }}>—</span>
          )}
        </td>
        <td style={{ ...TD, fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>{formatDate(laatste)}</td>
        <td style={{ ...TD, textAlign: "right" }}>
          {deelnemer.werknemer_id ? (
            <Link
              to={`/admin/werknemers/${deelnemer.werknemer_id}`}
              style={{ color: "var(--text-action)", fontWeight: "var(--fw-medium)" }}
              data-testid={`deelnemer-details-${testid}`}
            >
              Details
            </Link>
          ) : null}
        </td>
      </tr>

      {open ? (
        <tr data-testid={`deelnemer-signalen-${testid}`}>
          <td colSpan={6} style={{ ...TD, background: "var(--surface-sunken)" }}>
            <ul style={{ display: "grid", gap: "var(--ruimte-4)", listStyle: "none", margin: 0, padding: 0 }}>
              {deelnemer.items.map((f) => (
                <SignaalRegel key={f.flag_id} f={f} onAfhandelen={onAfhandelen} />
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** Eén signaal binnen de uitgeklapte deelnemer. */
function SignaalRegel({ f, onAfhandelen }) {
  const flagOpen = isOpen(f);
  const naam = signaalZin(f);
  const uitleg = SIGNAAL_UITLEG[f.regel_id];
  const locatie = locatieVan(f);

  const niveau = signaalNiveau(f);

  return (
    <li
      data-testid={`signaalregel-${f.flag_id}`}
      data-niveau={niveau}
      className={`niveau-${niveau}`}
      title={NIVEAU_UITLEG[niveau]}
      style={{
        display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "var(--ruimte-4)",
        borderBottom: "1px solid var(--border-subtle)",
        padding: "var(--ruimte-3) var(--ruimte-4)",
        borderRadius: "var(--radius)",
      }}
    >
      <div style={{ flex: "1 1 20rem", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-3)", flexWrap: "wrap" }}>
          <span style={{ color: "var(--text-strong)", fontWeight: "var(--fw-medium)" }} title={f.regel_id || undefined}>
            {naam}
          </span>
          <FaseBadge fase={f.fase} />
          <StatusMerkje open={flagOpen} testid={`pijnflag-status-${f.flag_id}`} />
        </div>
        {uitleg ? (
          <p
            data-testid={`signaalregel-${f.flag_id}-uitleg`}
            style={{
              margin: "var(--ruimte-2) 0 0", fontSize: "var(--fs-sm)",
              color: "var(--text-body)", whiteSpace: "pre-line",
            }}
          >
            {perTweeZinnen(uitleg)}
          </p>
        ) : null}
        {f.verbetering_zin ? (
          <p
            data-testid={`signaalregel-${f.flag_id}-verbetering`}
            style={{ margin: "var(--ruimte-2) 0 0", fontSize: "var(--fs-sm)", color: "var(--positief)" }}
          >
            {f.verbetering_zin}
          </p>
        ) : null}
        {/* Het pijncijfer stond hier tot 25-08-2026 nog een keer; het staat nu in de zin zelf. */}
        <p style={{ margin: "var(--ruimte-2) 0 0", fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
          {formatDate(f.doorgestuurd_op)}
          {locatie ? ` · ${locatie}` : ""}
        </p>
      </div>

      <div style={{ flex: "0 0 auto" }}>
        {flagOpen ? (
          <Button
            size="sm"
            onClick={() => onAfhandelen(f)}
            data-testid={`btn-afhandelen-${f.flag_id}`}
            style={{ background: "var(--positief)", color: "#fff" }}
          >
            Pak op
          </Button>
        ) : (
          <span data-testid={`done-${f.flag_id}`} style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
            Afgerond
          </span>
        )}
      </div>
    </li>
  );
}

/**
 * Eén kaart per deelnemer, met de signalen als regels erin.
 *
 * Tot 22-08-2026 kreeg elk signaal een eigen kaart. Bij één deelnemer met vijf signalen
 * stond zijn naam, klantnummer, "Bekijk klantprofiel" en de knop dus vijf keer op het
 * scherm — het leek alsof het om vijf mensen ging. Steven wees daarop.
 *
 * De groepering hierboven is per wérkgever. Bij een zzp'er is dat dezelfde naam als de
 * persoon, waardoor het extra verwarrend werd.
 */
/* De drempel-in-gewone-taal ("vanaf 3", "tot en met 4") is op 25-08-2026 vervallen. Het
   cijfer én de grens zitten nu in de zin zelf; de ruwe voorwaarde blijft in de tooltip
   staan voor wie moet uitzoeken waaróm een signaal afging. */

function PijnDeelnemerKaart({ deelnemer, onAfhandelen }) {
  const signalen = deelnemer.items;
  const open = signalen.filter((f) => !f.afgehandeld_op);
  // De signalen zitten achter een knop (Steven, 24-08-2026). Dicht als er niets meer
  // openstaat: dan hoeft dit blokje geen ruimte te vragen. Staat er wél iets open, dan
  // begint hij open — dat is waar je voor komt.
  const [toon, setToon] = useState(open.length > 0);
  /* Het hoogste pijncijfer bepaalt hoe dringend deze deelnemer is. Regels die niet over
     pijn gaan schrijven een 0 weg (zie migratie 024); die tellen hier niet mee. */
  const hoogste = signalen.reduce((h, f) => Math.max(h, f.pijncijfer || 0), 0);

  return (
    <article
      data-testid={`pijnflag-deelnemer-${deelnemer.werknemer_id || deelnemer.naam}`}
      style={{
        background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)", overflow: "hidden",
      }}
    >
      <header
        style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: "var(--ruimte-4)", padding: "var(--ruimte-5) var(--ruimte-6)",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "var(--fs-body)", fontWeight: "var(--fw-semi)", color: "var(--text-strong)" }}>
            {deelnemer.naam}
          </div>
          <div style={{ marginTop: 2, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
            <span style={{ fontFamily: "var(--font-mono)" }}>{deelnemer.klantnummer}</span>
            {" · "}
            {signalen.length} {signalen.length === 1 ? "signaal" : "signalen"}
            {open.length !== signalen.length ? ` · ${open.length} open` : null}
          </div>
          {deelnemer.werknemer_id ? (
            <Link
              to={`/admin/werknemers/${deelnemer.werknemer_id}`}
              style={{ display: "inline-block", marginTop: 4, fontSize: "var(--fs-xs)", color: "var(--text-action)" }}
            >
              Bekijk klantprofiel →
            </Link>
          ) : null}
        </div>
        {hoogste > 0 ? (
          <span
            title={`Hoogste pijncijfer: ${hoogste}`}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              minWidth: 30, height: 26, borderRadius: "var(--radius-pil)",
              fontSize: "var(--fs-body)", fontWeight: "var(--fw-bold)", fontVariantNumeric: "tabular-nums",
              ...pijnStijl(hoogste),
            }}
          >
            {hoogste}
          </span>
        ) : null}
      </header>

      <button
        type="button"
        onClick={() => setToon(!toon)}
        data-testid={`pijnflag-toon-${deelnemer.werknemer_id || deelnemer.naam}`}
        aria-expanded={toon}
        className="knop-compact"
        style={{
          display: "flex", alignItems: "center", gap: "var(--ruimte-3)", width: "100%",
          padding: "var(--ruimte-3) var(--ruimte-6)", background: "none", border: "none",
          borderTop: "1px solid var(--border-subtle)", cursor: "pointer",
          fontSize: "var(--fs-sm)", color: "var(--text-action)", fontFamily: "var(--font-tekst)",
          textAlign: "left",
        }}
      >
        <span
          className="uitklap-pijl"
          aria-hidden="true"
          style={{ transform: toon ? "rotate(90deg)" : "none" }}
        >
          &#9656;
        </span>
        {toon
          ? "Verberg signalen"
          : `Toon ${signalen.length} ${signalen.length === 1 ? "signaal" : "signalen"}`}
      </button>

      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: toon ? "block" : "none" }}>
        {signalen.map((f) => {
          const flagOpen = isOpen(f);
          return (
            <li
              key={f.flag_id}
              data-testid={`pijnflag-regel-${f.flag_id}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: "var(--ruimte-4)",
                padding: "var(--ruimte-4) var(--ruimte-6)",
                borderTop: "1px solid var(--border-zacht, var(--border-subtle))",
              }}
            >
              {/* Alleen een cijfer tonen als de regel écht over pijn gaat. Regels als
                  "klachten op drie plekken" schrijven een 0 weg; dat leest anders als
                  "pijn 0" terwijl er niets gemeten is. */}
              {f.pijncijfer > 0 ? (
                <span
                  style={{
                    flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center",
                    minWidth: 26, height: 22, borderRadius: "var(--radius-pil)",
                    fontSize: "var(--fs-xs)", fontWeight: "var(--fw-bold)", fontVariantNumeric: "tabular-nums",
                    ...pijnStijl(f.pijncijfer),
                  }}
                >
                  {f.pijncijfer}
                </span>
              ) : (
                <span style={{ flex: "0 0 auto", width: 26 }} aria-hidden="true" />
              )}

              <div style={{ minWidth: 0, flex: 1 }}>
                {/* Eén zin: wat er gemeten is en wat het mogelijk betekent. Tot 25-08-2026
                    stond hier de tekst uit de database ("Voet-keten verdacht: 3+ lichaamsdelen
                    klachten (Menz 2016)") met daaronder een regel "gemeten 5 · vanaf 3". Het
                    cijfer zit nu in de zin; de regelcode blijft als tooltip. */}
                <div
                  data-testid={`pijnflag-zin-${f.flag_id}`}
                  style={{ fontSize: "var(--fs-sm)", color: "var(--text-strong)" }}
                  title={[f.regel_id, f.drempel ? `drempel: ${f.drempel}` : null].filter(Boolean).join(" — ")}
                >
                  {signaalZin(f)}
                </div>
                {locatieVan(f) ? (
                  <div
                    data-testid={`pijnflag-detail-${f.flag_id}`}
                    style={{ marginTop: 2, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}
                  >
                    {locatieVan(f)}
                  </div>
                ) : null}
                {f.verbetering_zin ? (
                  <div
                    data-testid={`pijnflag-verbetering-${f.flag_id}`}
                    style={{ marginTop: 2, fontSize: "var(--fs-xs)", color: "var(--positief)" }}
                  >
                    {f.verbetering_zin}
                  </div>
                ) : null}
                <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--ruimte-3)" }}>
                  <FaseBadge fase={f.fase} />
                  {f.notificatie_categorie === "direct" ? (
                    <span style={{ ...MERKJE, background: "var(--alarm-zacht)", color: "var(--text-alarm)" }} title="Directe melding verstuurd">Direct</span>
                  ) : null}
                  <StatusMerkje open={flagOpen} />
                </div>
                {!flagOpen && f.actie_genomen ? (
                  <div style={{ marginTop: 4, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                    Afgehandeld: {f.actie_genomen}
                  </div>
                ) : null}
              </div>

              {flagOpen ? (
                <Button
                  size="sm"
                  onClick={() => onAfhandelen(f)}
                  data-testid={`btn-afhandelen-${f.flag_id}`}
                  style={{ flex: "0 0 auto", background: "var(--positief)", color: "#fff" }}
                >
                  Pak op
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

function AfhandelenModal({ flag, onClose, onSubmit, loading }) {
  const [actie, setActie] = useState("");
  const valid = actie.trim().length >= 5;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Signaal afhandelen"
      className="fulkra"
      data-testid="modal-afhandelen"
      style={{
        position: "fixed", inset: 0, zIndex: 40, display: "flex", alignItems: "center",
        justifyContent: "center", padding: "var(--ruimte-7)", background: "var(--overlay)",
      }}
    >
      <div style={{ width: "100%", maxWidth: 440 }}>
        <Card
          title="Signaal afhandelen"
          /* Wie ga ik afhandelen, en waarover? Naam en nummer, de plek als de aanmeter er
             één aanwees, de zin zelf, en het pijncijfer los — dat laatste alleen als er
             echt een cijfer gemeten is (regels als "klachten op drie plekken" schrijven
             een 0 weg, en "pijn 0" leest alsof er geen pijn was). */
          subtitle={[
            naamVan(flag),
            flag.klantnummer || flag.werknemer_id?.slice(0, 8),
            locatieVan(flag),
            signaalZin(flag),
            flag.pijncijfer > 0 ? `pijn ${flag.pijncijfer}` : null,
          ].filter(Boolean).join(" · ")}
        >
          <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
            <Label htmlFor="afhandelen-actie" required hint="Minimaal vijf tekens">Wat is gedaan</Label>
            <textarea
              id="afhandelen-actie"
              value={actie}
              onChange={(e) => setActie(e.target.value)}
              rows={4}
              data-testid="modal-afhandelen-actie"
              placeholder="Verslag van de actie, advies, doorverwijzing…"
              style={{
                width: "100%", borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)",
                background: "var(--wit)", color: "var(--text-strong)", padding: "var(--ruimte-4) var(--ruimte-5)",
                fontSize: "var(--fs-invoer-mobiel)", fontFamily: "var(--font-tekst)",
                lineHeight: "var(--lh-tekst)", boxShadow: "var(--schaduw-1)", outline: "none", resize: "vertical",
              }}
            />
          </div>
          <div style={{ marginTop: "var(--ruimte-7)", display: "flex", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
            <Button variant="secundair" onClick={onClose} data-testid="modal-afhandelen-cancel">Annuleren</Button>
            <Button
              disabled={!valid || loading}
              onClick={() => onSubmit({ actie_genomen: actie })}
              data-testid="modal-afhandelen-submit"
              style={{ background: "var(--positief)", color: "#fff" }}
            >
              {loading ? "Bezig…" : "Afhandelen"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
